import React, { useState, useEffect } from 'react';
import { NotificationStatus as NotificationStatusType } from '../../types/whatsapp';
import whatsappService from '../../services/whatsappService';

const NotificationStatus: React.FC = () => {
  const [status, setStatus] = useState<NotificationStatusType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let interval: number | undefined;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadStatus();
        interval = window.setInterval(loadStatus, 30000);
      } else if (interval) {
        window.clearInterval(interval);
        interval = undefined;
      }
    };

    handleVisibility();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (interval) window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const loadStatus = async () => {
    try {
      const notificationStatus = await whatsappService.getNotificationStatus();
      setStatus(notificationStatus);
    } catch (err) {
      console.error('Error loading notification status:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-red-800">Error al cargar estado</h3>
            <p className="text-sm text-red-600">No se pudo obtener el estado de las notificaciones</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusIcon = () => {
    if (!status.hasActiveConfiguration) {
      return (
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      );
    }
    if (status.shouldSendNotifications) {
      return (
        <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    return (
      <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  const getStatusColor = () => {
    if (!status.hasActiveConfiguration) return 'border-gray-200 bg-gray-50';
    if (status.shouldSendNotifications) return 'border-green-200 bg-green-50';
    return 'border-yellow-200 bg-yellow-50';
  };

  const getStatusText = () => {
    if (!status.hasActiveConfiguration) {
      return {
        title: 'Sin configuración activa',
        description: 'No hay una configuración de notificaciones activa'
      };
    }
    if (status.shouldSendNotifications) {
      return {
        title: 'Notificaciones activas',
        description: 'Las notificaciones están funcionando correctamente'
      };
    }
    if (status.isInWorkingHours) {
      return {
        title: 'Notificaciones pausadas',
        description: 'En horario laboral pero las notificaciones están deshabilitadas'
      };
    }
    return {
      title: 'Fuera de horario laboral',
      description: 'Las notificaciones están pausadas hasta el horario laboral'
    };
  };

  const statusText = getStatusText();

  return (
    <div className={`border rounded-lg p-6 ${getStatusColor()}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getStatusIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-lg font-medium text-gray-900">
            Estado de Notificaciones
          </h3>
          <div className="mt-2">
            <div className="flex items-center mb-2">
              <span className="text-sm font-medium text-gray-900 mr-2">
                {statusText.title}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {statusText.description}
            </p>
            
            {status.activeConfiguration && (
              <div className="text-xs text-gray-500 space-y-1">
                <div>
                  <span className="font-medium">Configuración activa:</span> {status.activeConfiguration.nombre}
                </div>
                <div>
                  <span className="font-medium">Horario:</span> {status.activeConfiguration.horaInicio} - {status.activeConfiguration.horaFin}
                </div>
                <div>
                  <span className="font-medium">Monitoreo:</span> cada {status.activeConfiguration.intervaloMonitoreoMinutos} min
                </div>
                <div>
                  <span className="font-medium">Reportes:</span> cada {status.activeConfiguration.intervaloReporteMinutos} min
                </div>
                <div>
                  <span className="font-medium">Contactos activos:</span> {status.activeConfiguration.contactos.filter(c => c.esActivo).length}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="ml-4">
          <button
            onClick={loadStatus}
            className="text-sm text-gray-500 hover:text-gray-700 focus:outline-none"
            title="Actualizar estado"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationStatus;
